import React, { createContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { labsAPI } from '../services/api';

export const LabsContext = createContext();

export const LabsProvider = ({ children }) => {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [userAchievements, setUserAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);

  // Debug: log environment info
  useEffect(() => {
    console.log('🔧 LabsContext Environment Info:', {
      hostname: window.location.hostname,
      isProduction: window.location.hostname !== 'localhost',
      API_BASE_URL: process.env.REACT_APP_API_URL || 'not set'
    });
  }, []);
  // Cargar rutas de labs
  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await labsAPI.getRoutes();
      setRoutes(data || []);
      console.log('📚 Labs cargados:', data?.length || 0);
    } catch (err) {
      console.error('❌ Error cargando labs:', err);
      setError(err.message);
      setRoutes([]); // Asegurar array vacío
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar progreso del usuario
  const fetchUserProgress = useCallback(async () => {
    if (!user) return;
    try {
      const data = await labsAPI.getUserProgress();
      setUserProgress(data);
    } catch (err) {
      setError(err.message);
    }
  }, [user]);

  // Cargar logros del usuario
  const fetchUserAchievements = useCallback(async () => {
    if (!user) return;
    try {
      const data = await labsAPI.getUserAchievements();
      setUserAchievements(data);
    } catch (err) {
      setError(err.message);
    }
  }, [user]);

  // Marcar nivel como completado
  const completeLevel = useCallback(async (routeId, levelId, flag) => {
    try {
      const result = await labsAPI.completeLevel(routeId, levelId, flag);
      if (result.success) {
        // Actualizar progreso local inmediatamente
        await fetchUserProgress();
        
        return { 
          success: true, 
          xpGained: result.xpGained,
          nextLevel: result.nextLevel,
          isNewCompletion: result.isNewCompletion
        };
      } else {
        // Return the failure result with message
        return { success: false, message: result.message || 'Flag incorrecto' };
      }
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message };
    }
  }, [fetchUserProgress]);

  // Obtener pista para un nivel
  const getHint = useCallback(async (routeId, levelId, hintIndex) => {
    try {
      const data = await labsAPI.getHint(routeId, levelId, hintIndex);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);
  // Cargar datos iniciales cuando hay usuario
  useEffect(() => {
    // Siempre cargar rutas públicas
    fetchRoutes();
    
    if (user) {
      fetchUserProgress();
      fetchUserAchievements();
    }
  }, [user, fetchRoutes, fetchUserProgress, fetchUserAchievements]);

  const value = {
    routes,
    userProgress,
    userAchievements,
    loading,
    error,
    selectedRoute,
    setSelectedRoute,
    selectedLevel,
    setSelectedLevel,
    fetchRoutes,
    fetchUserProgress,
    fetchUserAchievements,
    completeLevel,
    getHint,
  };

  return (
    <LabsContext.Provider value={value}>
      {children}
    </LabsContext.Provider>
  );
};
